// ---------------------------------------------------------------------------
// DebugOverlay — temporary debug panel rendered over the route screen.
//
// Shows: pool size, route counts by source, what won each slot with stats.
// Has a × dismiss button. Removed when route loop/dead-end debugging is
// complete. Grep for ROUTE_DIAGNOSTICS / DebugOverlay to find touch points.
// ---------------------------------------------------------------------------

import { useState } from "react";
import type { RouteDiagnostics, SlotDiagnostics } from "@/lib/routeDiagnostics";

interface Props {
  diagnostics: RouteDiagnostics | null;
}

function fmtSlot(label: string, slot: SlotDiagnostics | null): string {
  if (!slot) return `${label}: —`;
  const collapsed = slot.collapsed ? " (COLLAPSED)" : "";
  return `${label}: ${slot.source}${collapsed}\n  ${slot.distanceMi.toFixed(1)}mi · ${slot.durationMin.toFixed(0)}min · hwy=${slot.highwayPct.toFixed(0)}% · pl=${slot.pleasantScore.toFixed(0)}`;
}

export function DebugOverlay({ diagnostics }: Props) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !diagnostics) return null;

  const sources = Object.entries(diagnostics.sourceCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([k, v]) => `  ${k}: ${v}`)
    .join("\n");

  return (
    <div
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        right: 8,
        zIndex: 2147483647,
        background: "rgba(0, 0, 0, 0.9)",
        color: "white",
        fontFamily: "Menlo, monospace",
        fontSize: 11,
        lineHeight: 1.4,
        padding: "8px 28px 8px 10px",
        borderRadius: 6,
        whiteSpace: "pre-wrap",
        pointerEvents: "auto",
      }}
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        style={{
          position: "absolute",
          top: 4,
          right: 6,
          background: "transparent",
          color: "white",
          border: "none",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
          padding: 4,
        }}
        aria-label="Dismiss debug overlay"
      >
        ×
      </button>
      <strong>POOL: {diagnostics.poolSize} routes</strong>
      {"\n"}
      {sources}
      {"\n"}
      {fmtSlot("DIRECT", diagnostics.direct)}
      {"\n"}
      {fmtSlot("PLEASANT", diagnostics.pleasant)}
      {"\n"}
      {fmtSlot("ADVENTURE", diagnostics.adventure)}
    </div>
  );
}
